import React, { useMemo, useState } from 'react';
import API from '../ApiInterface';
import UpdateEmailSearchForm from '../UpdateEmailSearchForm';
import { _checkStrings } from '../common/sanitizeInputs';
import { codeBoxClass, inputClass, primaryButtonClass, secondaryButtonClass, sectionCardClass, sectionTitleClass, shellClass } from './ui';

const RequiredMark = () => <span className="ml-1 text-rose-500">*</span>;

const SCOPE_OPTIONS = [
  'activities:read', 'activities:write', 'activities:delete',
  'players:read', 'players:write', 'players:delete',
  'sports:read', 'sports:write', 'sports:delete',
  'measurements:read', 'profile:read', 'sessions:read',
];

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const baseApplicationForm = (partnerId) => ({
  applicationId: generateUuid(),
  partnerId,
  name: '',
  description: '',
  scopes: [],
  displayInDirectory: false,
  isDefault: false,
  logoURL: '',
  redirectURL: '',
  webHookURL: '',
});

const LOGO_UPLOAD_DELAY_MS = 750;
const LOGO_FETCH_DELAY_MS = 2000;
const LOGO_FETCH_MAX_ATTEMPTS = 5;

const SearchResults = ({ users, selectedUserId, onSelect, loading }) => {
  if (loading) {
    return <div className="px-4 py-6 text-sm text-slate-600">Searching users...</div>;
  }

  if (users.length === 0) {
    return <div className="px-4 py-6 text-sm text-slate-500">No users loaded.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-4 py-2 text-left font-semibold text-slate-700">Select</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-700">First Name</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-700">Last Name</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-700">Email</th>
            <th className="px-4 py-2 text-left font-semibold text-slate-700">User ID</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {users.map((user) => {
            const isSelected = selectedUserId === user.userId;

            return (
              <tr key={user.userId} className={isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}>
                <td className="px-4 py-3">
                  <label className="flex items-center gap-2 text-slate-700">
                    <input
                      type="radio"
                      name="selectedAssociatedUser"
                      checked={isSelected}
                      onChange={() => onSelect(user)}
                    />
                    <span>{isSelected ? 'Selected' : 'Choose'}</span>
                  </label>
                </td>
                <td className="px-4 py-3 text-slate-900">{user.firstName || '-'}</td>
                <td className="px-4 py-3 text-slate-900">{user.lastName || '-'}</td>
                <td className="px-4 py-3 text-slate-900">{user.email || '-'}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{user.userId || '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const StepOne = ({
  partnerId,
  partnerName,
  onPartnerNameChange,
  selectedUser,
  selectedUserId,
  users,
  searchLoading,
  searchError,
  onSearch,
  onSelectUser,
  onNext,
  submitError,
  canContinue,
}) => (
  <div className={shellClass}>
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Step 1 of 3</div>
      <h3 className="mt-1 text-lg font-semibold text-slate-900">Add Partner</h3>
    </div>

    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
      <div className="space-y-4">
        <div className={sectionCardClass}>
          <div className={`mb-3 ${sectionTitleClass}`}>Search user to associate<RequiredMark /></div>
          <UpdateEmailSearchForm handleSearchClick={onSearch} firstName="" lastName="" email="" />
        </div>

        {searchError && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {searchError}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <div className={sectionTitleClass}>Search results</div>
          </div>
          <SearchResults
            users={users}
            selectedUserId={selectedUserId}
            onSelect={onSelectUser}
            loading={searchLoading}
          />
        </div>
      </div>

      <div className={`h-fit ${sectionCardClass}`}>
        <div className={`mb-4 ${sectionTitleClass}`}>Partner details</div>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="partner-name">
            Partner name<RequiredMark />
          </label>
          <input
            id="partner-name"
            type="text"
            value={partnerName}
            onChange={(event) => onPartnerNameChange(event.target.value)}
            className={inputClass}
          />
        </div>

        <div className="mb-4">
          <div className="mb-1 text-sm font-medium text-slate-700">Partner ID</div>
          <div className={codeBoxClass}>
            {partnerId}
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-1 text-sm font-medium text-slate-700">Selected user</div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
            {selectedUser ? (
              <div className="space-y-1">
                <div className="font-medium text-slate-900">
                  {[selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') || selectedUser.email}
                </div>
                <div>{selectedUser.email || '-'}</div>
                <div className="font-mono text-xs">{selectedUser.userId}</div>
              </div>
            ) : (
              'No user selected.'
            )}
          </div>
        </div>

        {submitError && (
          <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {submitError}
          </div>
        )}

        <button type="button" onClick={onNext} disabled={!canContinue} className={primaryButtonClass}>
          Next
        </button>
      </div>
    </div>
  </div>
);

const ApplicationCard = ({
  application,
  index,
  onTextChange,
  onBooleanChange,
  onScopesChange,
}) => (
  <div className={sectionCardClass}>
    <div className="mb-4 flex items-center justify-between">
      <div>
        <div className={sectionTitleClass}>Application {index + 1}</div>
        <div className="font-mono text-xs text-slate-500">Application Id: {application.applicationId}</div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Name<RequiredMark /></label>
        <input
          type="text"
          value={application.name}
          onChange={(event) => onTextChange(index, 'name', event.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Description<RequiredMark /></label>
        <input
          type="text"
          value={application.description}
          onChange={(event) => onTextChange(index, 'description', event.target.value)}
          className={inputClass}
        />
      </div>

      <div className="md:col-span-2">
        <label className="mb-1 block text-sm font-medium text-slate-700">Scopes<RequiredMark /></label>
        <select
          multiple
          value={application.scopes}
          onChange={(event) => onScopesChange(index, event)}
          className={`${inputClass} h-40`}
        >
          {SCOPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Logo URL</label>
        <input
          type="text"
          value={application.logoURL}
          onChange={(event) => onTextChange(index, 'logoURL', event.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Redirect URL</label>
        <input
          type="text"
          value={application.redirectURL}
          onChange={(event) => onTextChange(index, 'redirectURL', event.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">WebHook URL</label>
        <input
          type="text"
          value={application.webHookURL}
          onChange={(event) => onTextChange(index, 'webHookURL', event.target.value)}
          className={inputClass}
        />
      </div>

      <div className="flex items-center gap-2 pt-7">
        <input
          id={`display-in-directory-${application.applicationId}`}
          type="checkbox"
          checked={application.displayInDirectory}
          onChange={(event) => onBooleanChange(index, 'displayInDirectory', event.target.checked)}
        />
        <label className="text-sm text-slate-700" htmlFor={`display-in-directory-${application.applicationId}`}>
          Display in directory
        </label>
      </div>

      <div className="flex items-center gap-2 pt-7">
        <input
          id={`is-default-${application.applicationId}`}
          type="checkbox"
          checked={application.isDefault}
          onChange={(event) => onBooleanChange(index, 'isDefault', event.target.checked)}
        />
        <label className="text-sm text-slate-700" htmlFor={`is-default-${application.applicationId}`}>
          Is default
        </label>
      </div>
    </div>
  </div>
);

const StepTwo = ({
  isExistingPartner,
  partnerName,
  applications,
  onTextChange,
  onBooleanChange,
  onScopesChange,
  onAddApplication,
  onBack,
  onFinish,
  onAddLogo,
  submitLoading,
  submitError,
  canContinue,
}) => (
  <div className={shellClass}>
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
          {isExistingPartner ? 'Add Applications' : 'Step 2 of 3'}
        </div>
        <h3 className="mt-1 text-lg font-semibold text-slate-900">
          {isExistingPartner ? `Add Applications to ${partnerName}` : 'Add Partner Applications'}
        </h3>
      </div>
      <button type="button" onClick={onAddApplication} className={secondaryButtonClass}>
        + Application
      </button>
    </div>

    {submitError && (
      <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {submitError}
      </div>
    )}

    <div className="space-y-4">
      {applications.map((application, index) => (
        <ApplicationCard
          key={application.applicationId}
          application={application}
          index={index}
          onTextChange={onTextChange}
          onBooleanChange={onBooleanChange}
          onScopesChange={onScopesChange}
        />
      ))}
    </div>

    <div className="mt-6 flex gap-2">
      <button type="button" onClick={onBack} disabled={submitLoading} className={secondaryButtonClass}>
        Back
      </button>
      <button type="button" onClick={onAddLogo} disabled={!canContinue || submitLoading} className={secondaryButtonClass}>
        Add Partner Logo
      </button>
      <button type="button" onClick={onFinish} disabled={!canContinue || submitLoading} className={primaryButtonClass}>
        {submitLoading ? 'Saving...' : 'Finish'}
      </button>
    </div>
  </div>
);

const StepThree = ({
  isExistingPartner,
  applications,
  logoFiles,
  onFileChange,
  onBack,
  onFinish,
  submitLoading,
  submitError,
  progress,
  progressLabel,
  canFinish,
}) => (
  <div className={shellClass}>
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
        {isExistingPartner ? 'Add Logos' : 'Step 3 of 3'}
      </div>
      <h3 className="mt-1 text-lg font-semibold text-slate-900">Add Partner Logos</h3>
    </div>

    {submitError && (
      <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {submitError}
      </div>
    )}

    <div className="space-y-4">
      {applications.map((application, index) => (
        <div key={application.applicationId} className={sectionCardClass}>
          <div className="mb-2">
            <div className="text-sm font-semibold text-slate-900">
              {application.name.trim() || `Application ${index + 1}`}
            </div>
            <div className="font-mono text-xs text-slate-500">Application Id: {application.applicationId}</div>
          </div>
          <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor={`partner-logo-file-${application.applicationId}`}>
            Upload image file<RequiredMark />
          </label>
          <input
            id={`partner-logo-file-${application.applicationId}`}
            type="file"
            accept="image/*"
            onChange={(event) => onFileChange(application.applicationId, event.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-700"
          />
        </div>
      ))}
    </div>

    <div className="mt-4">
      <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
        <span>Progress</span>
        <span>{progressLabel}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-blue-600 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>
    </div>

    <div className="mt-6 flex gap-2">
      <button type="button" onClick={onBack} disabled={submitLoading} className={secondaryButtonClass}>
        Back
      </button>
      <button type="button" onClick={onFinish} disabled={!canFinish || submitLoading} className={primaryButtonClass}>
        {submitLoading ? 'Finishing...' : 'Finish'}
      </button>
    </div>
  </div>
);

const CompletionStep = ({ applications, apiKeys, loading, error, onClose }) => {
  const handleCopy = async (value) => {
    if (!value || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (copyError) {
      console.error(copyError);
    }
  };

  return (
    <div className={shellClass}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Completed</div>
          <h3 className="mt-1 text-lg font-semibold text-slate-900">Partner onboarding complete</h3>
        </div>
        <button type="button" onClick={onClose} disabled={loading} className={primaryButtonClass}>
          Close
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {applications.map((application, index) => {
          const apiKey = apiKeys[application.applicationId] || '';

          return (
            <div key={application.applicationId} className={sectionCardClass}>
              <div className="mb-2">
                <div className="text-sm font-semibold text-slate-900">
                  {application.name.trim() || `Application ${index + 1}`}
                </div>
                <div className="font-mono text-xs text-slate-500">Application Id: {application.applicationId}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">API Key</div>
                  <button
                    type="button"
                    onClick={() => handleCopy(apiKey)}
                    disabled={!apiKey}
                    className={secondaryButtonClass}
                  >
                    Copy
                  </button>
                </div>
                <code className="block overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-slate-800">
                  {loading
                    ? 'Loading API key...'
                    : apiKey || 'API key could not be fetched.'}
                </code>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OnboardPartner = ({ token, onClose, existingPartner }) => {
  const isExistingPartner = Boolean(existingPartner?.partnerId);
  const [step, setStep] = useState(isExistingPartner ? 2 : 1);
  const [partnerId] = useState(() => existingPartner?.partnerId || generateUuid());
  const [partnerName, setPartnerName] = useState(() => existingPartner?.partnerName || '');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(() => existingPartner?.userId || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [applications, setApplications] = useState(() => [baseApplicationForm(partnerId)]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [stepTwoLoading, setStepTwoLoading] = useState(false);
  const [stepThreeLoading, setStepThreeLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [stepOneError, setStepOneError] = useState('');
  const [stepTwoError, setStepTwoError] = useState('');
  const [stepThreeError, setStepThreeError] = useState('');
  const [completionError, setCompletionError] = useState('');
  const [completionLoading, setCompletionLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState({});
  const [logoFiles, setLogoFiles] = useState({});
  const [logoProgress, setLogoProgress] = useState(0);
  const [logoProgressLabel, setLogoProgressLabel] = useState('Waiting to start');

  const canContinueStepOne = useMemo(() => Boolean(selectedUserId && partnerName.trim()), [partnerName, selectedUserId]);
  const canContinueStepTwo = useMemo(() => {
    return applications.every((application) => (
      application.applicationId &&
      application.partnerId &&
      application.name.trim() &&
      application.description.trim() &&
      Array.isArray(application.scopes) &&
      application.scopes.length > 0
    ));
  }, [applications]);
  const canFinishStepThree = useMemo(() => {
    return applications.every((application) => Boolean(logoFiles[application.applicationId]));
  }, [applications, logoFiles]);

  const handleSearchClick = async (firstName, lastName, email) => {
    if (!token) {
      setSearchError('Missing token. Open this page from the dashboard menu after signing in.');
      return;
    }

    setSearchLoading(true);
    setSearchError('');

    const safeFirstName = _checkStrings(firstName).trim();
    const safeLastName = _checkStrings(lastName).trim();
    const safeEmail = _checkStrings(email).trim();

    try {
      const response = await API.getSearchUsers(token, safeFirstName, safeLastName, safeEmail, 25, 0);
      const nextUsers = Array.isArray(response?.data) ? response.data : [];
      setUsers(nextUsers);

      const selectedStillExists = nextUsers.some((user) => user.userId === selectedUserId);
      if (!selectedStillExists) {
        setSelectedUserId('');
        setSelectedUser(null);
      }
    } catch (error) {
      setUsers([]);
      setSearchError(error?.message || 'Failed to search users.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUserId(user?.userId || '');
    setSelectedUser(user || null);
  };

  const handleStepOneNext = () => {
    if (!canContinueStepOne || !token) return;
    setStepOneError('');
    setApplications((prev) => prev.map((application) => ({ ...application, partnerId })));
    setStep(2);
  };

  const updateApplication = (index, updater) => {
    setApplications((prev) =>
      prev.map((application, currentIndex) =>
        currentIndex === index ? updater(application) : application
      )
    );
  };

  const handleApplicationTextChange = (index, key, value) => {
    updateApplication(index, (application) => ({ ...application, [key]: value }));
  };

  const handleApplicationBooleanChange = (index, key, value) => {
    updateApplication(index, (application) => ({ ...application, [key]: value }));
  };

  const handleScopesChange = (index, event) => {
    const scopes = Array.from(event.target.selectedOptions).map((option) => option.value);
    updateApplication(index, (application) => ({ ...application, scopes }));
  };

  const handleAddApplication = () => {
    setApplications((prev) => [...prev, baseApplicationForm(partnerId)]);
  };

  const buildApplicationBody = (application) => {
    const body = {
      applicationId: application.applicationId,
      partnerId: application.partnerId,
      name: application.name.trim(),
      description: application.description.trim(),
      scopes: application.scopes,
      displayInDirectory: application.displayInDirectory,
      isDefault: application.isDefault,
    };

    ['logoURL', 'redirectURL', 'webHookURL'].forEach((field) => {
      const value = application[field]?.trim();
      if (value) {
        body[field] = value;
      }
    });

    return body;
  };

  const createPartnerAndApplications = async (setError, setLoading) => {
    if (!isExistingPartner) {
      const addPartnerResponse = await API.addPartner(token, partnerId, partnerName.trim(), selectedUserId);
      if (!addPartnerResponse || addPartnerResponse.status < 200 || addPartnerResponse.status >= 300) {
        setError(
          addPartnerResponse?.data?.errorDescription || addPartnerResponse?.data?.message || 'Failed to add partner.'
        );
        setLoading(false);
        return false;
      }
    }

    for (const application of applications) {
      const addApplicationResponse = await API.addPartnerApplication(token, buildApplicationBody(application));
      if (!addApplicationResponse || addApplicationResponse.status < 200 || addApplicationResponse.status >= 300) {
        setError(
          addApplicationResponse?.data?.errorDescription ||
          addApplicationResponse?.data?.message ||
          `Failed to add application ${application.name || application.applicationId}.`
        );
        setLoading(false);
        return false;
      }
    }

    return true;
  };

  const loadApiKeys = async () => {
    setCompletionLoading(true);
    setCompletionError('');

    const nextApiKeys = {};

    for (const application of applications) {
      const response = await API.getPartnerApiKey(token, partnerId, application.applicationId);
      if (!response || response.status < 200 || response.status >= 300) {
        setCompletionError(
          response?.data?.errorDescription ||
          response?.data?.message ||
          `Failed to fetch API key for ${application.name || application.applicationId}.`
        );
        setCompletionLoading(false);
        return;
      }

      nextApiKeys[application.applicationId] = response?.data?.apiKey || '';
    }

    setApiKeys(nextApiKeys);
    setCompletionLoading(false);
    setStep(4);
  };

  const handleStepTwoFinish = async () => {
    if (!canContinueStepTwo || !token) return;

    setStepTwoLoading(true);
    setStepTwoError('');

    const ok = await createPartnerAndApplications(setStepTwoError, setStepTwoLoading);
    if (!ok) return;

    setStepTwoLoading(false);
    await loadApiKeys();
  };

  const handleGoToStepThree = () => {
    if (!canContinueStepTwo || !token) return;
    setStepTwoError('');
    setStep(3);
  };

  const handleLogoFileChange = (applicationId, file) => {
    setLogoFiles((prev) => ({ ...prev, [applicationId]: file }));
  };

  const handleStepThreeFinish = async () => {
    if (!canFinishStepThree || !token) return;

    setStepThreeLoading(true);
    setStepThreeError('');
    setLogoProgress(5);
    setLogoProgressLabel(isExistingPartner ? 'Adding applications' : 'Adding partner');

    const ok = await createPartnerAndApplications(setStepThreeError, setStepThreeLoading);
    if (!ok) {
      setLogoProgress(0);
      setLogoProgressLabel('Creation failed');
      return;
    }

    const totalSteps = applications.length * 3 + (isExistingPartner ? 1 : 2);
    let completedSteps = applications.length + (isExistingPartner ? 0 : 1);

    const updateProgress = (label) => {
      const progress = Math.min(100, Math.round((completedSteps / totalSteps) * 100));
      setLogoProgress(progress);
      setLogoProgressLabel(label);
    };

    updateProgress(isExistingPartner ? 'Created applications' : 'Created partner and applications');

    for (const application of applications) {
      const uploadResponse = await API.addPartnerLogo(
        token,
        logoFiles[application.applicationId],
        partnerId,
        application.applicationId,
        partnerName.trim()
      );

      if (!uploadResponse || uploadResponse.status < 200 || uploadResponse.status >= 300) {
        setStepThreeError(
          uploadResponse?.data?.errorDescription ||
          uploadResponse?.data?.message ||
          `Failed to upload logo for ${application.name || application.applicationId}.`
        );
        setStepThreeLoading(false);
        setLogoProgressLabel('Upload failed');
        return;
      }

      completedSteps += 1;
      updateProgress(`Uploaded logo for ${application.name || application.applicationId}`);
      await sleep(LOGO_UPLOAD_DELAY_MS);
    }

    setLogoProgressLabel('Waiting for logo processing');
    await sleep(LOGO_FETCH_DELAY_MS);

    let logos = [];
    let fetchedAllLogos = false;

    for (let attempt = 1; attempt <= LOGO_FETCH_MAX_ATTEMPTS; attempt += 1) {
      const getLogoResponse = await API.getPartnerLogo(token, partnerId);
      if (!getLogoResponse || getLogoResponse.status < 200 || getLogoResponse.status >= 300) {
        if (attempt === LOGO_FETCH_MAX_ATTEMPTS) {
          setStepThreeError(
            getLogoResponse?.data?.errorDescription || getLogoResponse?.data?.message || 'Failed to fetch uploaded logos.'
          );
          setStepThreeLoading(false);
          setLogoProgressLabel('Fetch failed');
          return;
        }

        await sleep(LOGO_FETCH_DELAY_MS);
        continue;
      }

      logos = Array.isArray(getLogoResponse?.data?.logos) ? getLogoResponse.data.logos : [];
      fetchedAllLogos = applications.every((application) =>
        logos.some(
          (logo) =>
            logo?.partnerId === partnerId &&
            logo?.applicationId === application.applicationId &&
            logo?.logoURL
        )
      );

      if (fetchedAllLogos) {
        break;
      }

      if (attempt < LOGO_FETCH_MAX_ATTEMPTS) {
        setLogoProgressLabel(`Waiting for logos to be available (${attempt}/${LOGO_FETCH_MAX_ATTEMPTS})`);
        await sleep(LOGO_FETCH_DELAY_MS);
      }
    }

    if (!fetchedAllLogos) {
      setStepThreeError('Uploaded logos were not available in time. Please try again.');
      setStepThreeLoading(false);
      setLogoProgressLabel('Logo lookup failed');
      return;
    }

    completedSteps += 1;
    updateProgress('Fetched uploaded logos');

    for (const application of applications) {
      const matchingLogo = logos.find(
        (logo) => logo?.partnerId === partnerId && logo?.applicationId === application.applicationId
      );

      if (!matchingLogo?.logoURL) {
        setStepThreeError(`Uploaded logo was not found for ${application.name || application.applicationId}.`);
        setStepThreeLoading(false);
        setLogoProgressLabel('Logo lookup failed');
        return;
      }

      const updateResponse = await API.updatePartnerApp(token, application.applicationId, { logoURL: matchingLogo.logoURL });
      if (!updateResponse || updateResponse.status < 200 || updateResponse.status >= 300) {
        setStepThreeError(
          updateResponse?.data?.errorDescription ||
          updateResponse?.data?.message ||
          `Failed to update application ${application.name || application.applicationId} with logo.`
        );
        setStepThreeLoading(false);
        setLogoProgressLabel('Update failed');
        return;
      }

      completedSteps += 1;
      updateProgress(`Updated logo for ${application.name || application.applicationId}`);
    }

    setLogoProgress(100);
    setLogoProgressLabel('Completed');

    setStepThreeLoading(false);
    await loadApiKeys();
  };

  if (step === 1) {
    return (
      <StepOne
        partnerId={partnerId}
        partnerName={partnerName}
        onPartnerNameChange={setPartnerName}
        selectedUser={selectedUser}
        selectedUserId={selectedUserId}
        users={users}
        searchLoading={searchLoading}
        searchError={searchError}
        onSearch={handleSearchClick}
        onSelectUser={handleSelectUser}
        onNext={handleStepOneNext}
        submitError={stepOneError}
        canContinue={canContinueStepOne}
      />
    );
  }

  if (step === 2) {
    return (
      <StepTwo
        isExistingPartner={isExistingPartner}
        partnerName={partnerName}
        applications={applications}
        onTextChange={handleApplicationTextChange}
        onBooleanChange={handleApplicationBooleanChange}
        onScopesChange={handleScopesChange}
        onAddApplication={handleAddApplication}
        onBack={() => {
          if (isExistingPartner) {
            if (typeof onClose === 'function') {
              onClose();
            }
            return;
          }
          setStep(1);
        }}
        onFinish={handleStepTwoFinish}
        onAddLogo={handleGoToStepThree}
        submitLoading={stepTwoLoading}
        submitError={stepTwoError}
        canContinue={canContinueStepTwo}
      />
    );
  }

  if (step === 4) {
    return (
      <CompletionStep
        applications={applications}
        apiKeys={apiKeys}
        loading={completionLoading}
        error={completionError}
        onClose={() => {
          if (typeof onClose === 'function') {
            onClose();
          }
        }}
      />
    );
  }

  return (
    <StepThree
      isExistingPartner={isExistingPartner}
      applications={applications}
      logoFiles={logoFiles}
      onFileChange={handleLogoFileChange}
      onBack={() => setStep(2)}
      onFinish={handleStepThreeFinish}
      submitLoading={stepThreeLoading}
      submitError={stepThreeError}
      progress={logoProgress}
      progressLabel={logoProgressLabel}
      canFinish={canFinishStepThree}
    />
  );
};

export default OnboardPartner;
